import api from "@/api/axios";
import type {
  AppleTokenLoginPayload,
  CustomerAuthResponse,
  CustomerLoginPayload,
  CustomerRegisterPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  VerifyEmailPayload,
  VerifyEmailResponse,
  VerifyOtpResponse,
  VerifyResetOtpPayload,
} from "@/types/customer-auth.types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// ============================================
// Customer Auth Service
// ============================================

export const customerAuthService = {
  /**
   * Register new customer account
   * Account will be in Pending state until email is verified
   */
  register: async (payload: CustomerRegisterPayload): Promise<void> => {
    await api.post("/auth-customer/register", payload);
  },

  /**
   * Verify email with OTP code
   * Returns user data after successful verification
   */
  verifyEmail: async (
    payload: VerifyEmailPayload,
  ): Promise<VerifyEmailResponse> => {
    const response = await api.post<VerifyEmailResponse>(
      "/auth-customer/verify-email",
      payload,
    );
    return response.data;
  },

  /**
   * Login with email and password
   * Returns user data and tokens
   */
  login: async (
    payload: CustomerLoginPayload,
  ): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>(
      "/auth-customer/login",
      payload,
    );
    return response.data;
  },

  /**
   * Request password reset OTP
   * Sends OTP to user's email
   */
  forgotPassword: async (
    payload: ForgotPasswordPayload,
  ): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>(
      "/auth-customer/otp/forgot-password",
      payload,
    );
    return response.data;
  },

  /**
   * Verify reset password OTP
   * Returns whether OTP is valid
   */
  verifyResetOtp: async (
    payload: VerifyResetOtpPayload,
  ): Promise<VerifyOtpResponse> => {
    const response = await api.post<VerifyOtpResponse>(
      "/auth-customer/otp/verify-reset",
      payload,
    );
    return response.data;
  },

  /**
   * Reset password with OTP
   */
  resetPassword: async (
    payload: ResetPasswordPayload,
  ): Promise<ResetPasswordResponse> => {
    const response = await api.post<ResetPasswordResponse>(
      "/auth-customer/otp/reset-password",
      payload,
    );
    return response.data;
  },

  /**
   * Login with Apple ID token (client-side Sign in with Apple)
   */
  appleTokenLogin: async (
    payload: AppleTokenLoginPayload,
  ): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>(
      "/auth-customer/apple/token",
      payload,
    );
    return response.data;
  },

  /**
   * Resend verification email
   * Re-registers the user to trigger new OTP email
   */
  resendVerificationEmail: async (
    email: string,
    password: string,
    fullName: string,
  ): Promise<void> => {
    await api.post("/auth-customer/register", {
      email,
      password,
      full_name: fullName,
    });
  },
};

// ============================================
// Social Login URLs
// ============================================

export const socialLoginUrls = {
  google: `${API_BASE_URL}/auth-customer/google`,
  facebook: `${API_BASE_URL}/auth-customer/facebook`,
  apple: `${API_BASE_URL}/auth-customer/apple`,
};

// ============================================
// Token Management (for customer auth)
// ============================================

const TOKEN_KEY = "customer_access_token";
const REFRESH_TOKEN_KEY = "customer_refresh_token";
const USER_KEY = "customer_user";
const TYPE_LOGIN_KEY = "type_login";

export const customerTokenService = {
  getType(): string | null {
    return localStorage.getItem(TYPE_LOGIN_KEY);
  },

  setType(type: "store" | "customer"): void {
    localStorage.setItem(TYPE_LOGIN_KEY, type);
  },

  getAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  setAccessToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  setUser: (user: unknown): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  saveAuthData: (data: CustomerAuthResponse): void => {
    customerTokenService.setAccessToken(data.access_token);
    customerTokenService.setRefreshToken(data.refresh_token);
    customerTokenService.setUser(data.user);
    customerTokenService.setType("customer");
  },

  clearAuth: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TYPE_LOGIN_KEY);
  },

  isAuthenticated: (): boolean => {
    return !!customerTokenService.getAccessToken();
  },
};

export default customerAuthService;
